// src/pages/TestManagementPage/ManageTestsTab.js
// 目的：テスト管理一覧に「開始日時」「終了日時」列を追加し、確認しやすくする
// 仕様：Firestore の Timestamp / Date / number に対応して時刻を表示
//       ソート可能（開始・終了の昇順/降順）
//       表示形式は YYYY/MM/DD HH:mm、未設定は「未設定」
// 注意：tests コレクションの各ドキュメントに startTime / endTime が存在しない場合は「未設定」

import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, App, Popconfirm, Spin } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { db } from '../../firebase-config';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import dayjs from 'dayjs';

/* ========= ヘルパー：Firestore 時刻 → 表示 ========= */

// Firestore Timestamp / Date / number をミリ秒へ変換
const toMillis = (ts) => {
  if (!ts) return 0;
  if (typeof ts === 'number') return ts;
  if (ts?.toDate) return ts.toDate().getTime();
  if (ts instanceof Date) return ts.getTime();
  return 0;
};

// 表示用フォーマット
const fmt = (ts) => {
  const ms = toMillis(ts);
  return ms ? dayjs(ms).format('YYYY/MM/DD HH:mm') : '未設定';
};

const ManageTestsTab = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const { message } = App.useApp();

  // テスト一覧取得
  const fetchTests = useCallback(async () => {
    setLoading(true);
    try {
      const testsCollectionRef = collection(db, 'tests');
      const data = await getDocs(testsCollectionRef);
      setTests(data.docs.map((d) => ({ ...d.data(), id: d.id })));
    } catch (error) {
      console.error(error);
      message.error('テスト一覧を取得できませんでした。');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    fetchTests();
  }, [fetchTests]);

  // 削除
  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'tests', id));
      setTests((prev) => prev.filter((t) => t.id !== id));
      message.success('テストを削除しました。');
    } catch (error) {
      console.error(error);
      message.error('削除に失敗しました。');
    }
  };

  const columns = [
    {
      title: 'テスト名',
      dataIndex: 'testName',
      key: 'testName',
      sorter: (a, b) => (a.testName || '').localeCompare(b.testName || ''),
    },
    {
      title: 'レベル',
      dataIndex: 'levelId',
      key: 'levelId',
      render: (levelId) => (levelId ? levelId.replace('level', 'レベル ') : '未設定'),
      align: 'center',
    },
    {
      title: '問題数',
      key: 'questionCount',
      align: 'center',
      render: (_, record) => record.questions?.length || 0,
      sorter: (a, b) => (a.questions?.length || 0) - (b.questions?.length || 0),
    },
    {
      title: '合計点',
      dataIndex: 'totalScore',
      key: 'totalScore',
      align: 'center',
      sorter: (a, b) => Number(a.totalScore || 0) - Number(b.totalScore || 0),
    },
    // 追加：開始日時
    {
      title: '開始日時',
      dataIndex: 'startTime',
      key: 'startTime',
      align: 'center',
      render: (v) => fmt(v),
      sorter: (a, b) => toMillis(a.startTime) - toMillis(b.startTime),
    },
    // 追加：終了日時
    {
      title: '終了日時',
      dataIndex: 'endTime',
      key: 'endTime',
      align: 'center',
      render: (v) => fmt(v),
      sorter: (a, b) => toMillis(a.endTime) - toMillis(b.endTime),
    },
    {
      title: '操作',
      key: 'action',
      align: 'center',
      render: (_, record) => (
        <Space>
          <Link to={`/edit-test/${record.id}`}>
            <Button icon={<EditOutlined />}>編集</Button>
          </Link>
          <Popconfirm
            title="テストを削除しますか？"
            description="このテストを削除してもよろしいですか？"
            onConfirm={() => handleDelete(record.id)}
            okText="削除"
            cancelText="キャンセル"
          >
            <Button icon={<DeleteOutlined />} danger>
              削除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) return <Spin />;

  return (
    <div>
      <Table
        columns={columns}
        dataSource={tests.map((t) => ({ ...t, key: t.id }))}
        bordered
      />
    </div>
  );
};

export default ManageTestsTab;
